import React, {useState} from 'react';

import Link from './Link';
import Button from './ButtonNew';
import Modal from './ModalNew';
import StarRating from './StarRating';
import {StyledCheckbox as Checkbox, StyledTextArea as TextArea} from './Forms';
import Logo from '../../assets/images/logo.svg';

import {extractAPIError} from '../util';
import {submitFeedback} from '../api/api';

export const shouldShowFeedbackModal = (currentUser, hasSubscription) => {
  if (!hasSubscription || !currentUser?.membershipCreatedAt) return false;

  const createdAt = new Date(currentUser.membershipCreatedAt);
  const now = new Date();

  // Check if 3 days have passed since membership creation
  const threeDaysAfter = new Date(createdAt);
  threeDaysAfter.setDate(createdAt.getDate() + 3);
  const isThreeDaysPassed = now >= threeDaysAfter;

  if (!isThreeDaysPassed) return false;

  // Check when the feedback modal was last seen
  const seenAt = localStorage.getItem('seenFeedbackModalAt');
  if (!seenAt) return true;

  const seenDate = new Date(seenAt);
  return seenDate < createdAt;
};

const FeedbackModalPages = {
  RATING: 'Rating',
  WRITTEN_FEEDBACK: 'Written Feedback',
  SUCCESS: 'Success',
};

const FeedbackModal = ({isOpen, onClose, courseSlug}) => {
  const [feedbackId, setFeedbackId] = useState(null);
  const [error, setError] = useState(null);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [page, setPage] = useState(FeedbackModalPages.RATING);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCourse = Boolean(courseSlug); // course slug = track slug || course slug

  const handleInitialSubmit = async () => {
    if (rating == null) return;

    try {
      const {id} = await submitFeedback({rating, product: courseSlug});
      setFeedbackId(id);
      setPage(FeedbackModalPages.WRITTEN_FEEDBACK);
    } catch (err) {
      setError(extractAPIError(err));
    }
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      await submitFeedback({
        feedbackId, rating, product: courseSlug, feedback, isAnonymous,
      });
      setPage(FeedbackModalPages.SUCCESS);
    } catch (err) {
      setError(extractAPIError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    localStorage.setItem('seenFeedbackModalAt', Date.now());
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      modalClassName="sm:max-w-sm"
      showCloseButton
    >
      {page === FeedbackModalPages.RATING && (
        <div className="flex flex-col items-center gap-y-3">
          <img src={Logo.src} alt="Exponent Logo" className="h-12 w-12" />
          <div className="text-lg font-medium">
            {`Enjoying ${isCourse ? 'this course' : 'Exponent'}?`}
          </div>
          <div className="text-base text-gray-600">
            Rate your experience to gain 10
            {' '}
            <Link href="/karma" target="_blank" className="text-indigo font-medium">
              karma
            </Link>
            !
          </div>
          <StarRating initialRating={5} onRating={setRating} starClassName="h-10 w-10" />
          <Button onClick={handleInitialSubmit} className="w-full mt-2">
            Submit
          </Button>
        </div>
      )}

      {page === FeedbackModalPages.WRITTEN_FEEDBACK && (
        <div className="flex flex-col gap-y-2">
          {rating >= 4 ? (
            <>
              <div className="text-lg text-center font-semibold">
                Share your experience
              </div>
              <div className="font-medium text-gray-800">
                {`Awesome! What are you enjoying about ${isCourse ? 'this course' : 'Exponent'}?`}
              </div>
              <TextArea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
              <div className="flex items-center text-gray-800">
                <Checkbox
                  id="anonymous-checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <label htmlFor="anonymous-checkbox" className="ml-2 font-medium text-sm">Keep my review anonymous</label>
              </div>
            </>
          ) : (
            <>
              <div className="text-lg text-center font-semibold">
                Share your experience
              </div>
              <div className="font-medium text-gray-800">
                Sorry to hear that. How can we do better?
              </div>
              <TextArea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="mb-2"
              />
            </>
          )}
          <Button onClick={handleFinalSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      )}

      {page === FeedbackModalPages.SUCCESS && (
        <div className="text-center">
          <div className="text-lg font-semibold mb-4">Thank you for your feedback!</div>
          <div className="text-gray-600">
            {rating >= 4
              ? 'We are thrilled to hear you are enjoying Exponent!'
              : 'Your feedback helps us make the experience better for everyone.'}
          </div>
        </div>
      )}

      {error && (
        <div className="text-red-600 text-center mt-4">
          {error}
        </div>
      )}
    </Modal>
  );
};

export default FeedbackModal;
